<template>
  <v-dialog
    v-model="isOpen"
    fullscreen
    @keydown.escape="closeTaskDetailAndClearRoute"
    @after-leave="resetDialogProps">
    <v-card
      class="task-detail-card d-flex flex-column h-100"
      data-testid="circe-task-detail-dialog">
      <template v-if="taskId">
        <template v-if="isLoading">
          <div class="d-flex justify-center align-center h-100 px-3">
            <v-progress-linear indeterminate color="primary"></v-progress-linear>
          </div>
        </template>
        <template v-else-if="task?.id">
          <v-card-title
            class="d-flex align-center justify-space-between flex-shrink-0"
            :class="task.status">
            <v-btn
              class="back-btn"
              size="small"
              icon
              variant="flat"
              title="close"
              data-testid="circe-task-detail-back"
              @click="closeTaskDetailAndClearRoute">
              <v-icon icon="mdi-arrow-left"></v-icon>
            </v-btn>
            <div
              class="d-flex align-center gap-3 typography-body-md-bold text--color-text-secondary">
              <v-icon
                size="small"
                v-if="statusIcon"
                :icon="statusIcon"
                :color="statusIconColor || 'currentColor'"></v-icon>
              <span
                v-if="isTaskInProgress && inProgressEvent?.rawAt"
                data-testid="circe-task-elapsed-time">
                <elapsed-time
                  format="hh:mm:ss"
                  :update-interval="1000"
                  :from="inProgressEvent?.rawAt"
                  :timezone="warehouseTimezone" />
              </span>
              <span v-else class="typography-body-md-bold text--color-text-secondary">
                {{ statusLabel }}
              </span>
            </div>
            <span class="typography-body-md-bold"># {{ taskNumber }}</span>
          </v-card-title>

          <v-card-text
            class="d-flex flex-column flex-grow-1 overflow-hidden"
            style="padding: 0 !important">
            <div
              v-if="(isTaskCompleted && completedEvent) || (isTaskCancelled && cancelledEvent)"
              class="d-flex flex-row gap-1 align-center justify-center px-2 py-1 flex-shrink-0 completion-banner">
              <span class="typography-caption-bold">
                by
                {{ isTaskCompleted ? completedEvent?.by : cancelledEvent?.by }}
              </span>
              <span class="typography-caption">
                on
                {{ isTaskCompleted ? completedEvent?.at : cancelledEvent?.at }}
              </span>
            </div>

            <div class="d-flex flex-column flex-grow-1 overflow-hidden">
              <custom-tabs v-model="activeTab" class="mx-5 my-0 flex-shrink-0">
                <v-tab value="task">Task</v-tab>
                <v-tab value="activity" data-testid="circe-task-detail-activity-tab">
                  <span class="d-none d-md-flex">Comments & Activity</span>
                  <span class="d-flex d-md-none">Activity</span>
                </v-tab>
              </custom-tabs>

              <v-tabs-window
                v-model="activeTab"
                class="custom-tabs-window flex-grow-1 overflow-y-auto">
                <v-tabs-window-item
                  value="task"
                  :key="`task-${task.id}-${task.lastChangedDateTime || ''}`">
                  <div class="px-5 py-3 bg--color-neutral-10 h-100">
                    <move-task-details
                      v-if="moveTask"
                      :task="moveTask"
                      @allow-complete="handleAllowComplete"></move-task-details>
                    <check-task-details
                      v-if="checkTask"
                      :task="checkTask"
                      @allow-complete="handleAllowComplete" />
                  </div>
                </v-tabs-window-item>

                <v-tabs-window-item value="activity" class="flex-grow-1">
                  <div class="d-flex flex-column h-100 bg--color-neutral-10">
                    <div
                      ref="activityScrollRef"
                      class="activity-scroll flex-grow-1 overflow-y-auto pt-0 px-5 pb-5">
                      <yard-task-timeline
                        :task-id="taskId"
                        :warehouse-timezone="warehouseTimezone" />
                    </div>
                    <v-divider class="flex-shrink-0"></v-divider>
                    <div class="pa-4 flex-shrink-0">
                      <yard-task-comment-form
                        related-entity="YardTask"
                        :related-id="taskId"
                        @comment="handleCommentAdded()" />
                    </div>
                  </div>
                </v-tabs-window-item>
              </v-tabs-window>
            </div>

            <template v-if="activeTab === 'task'">
              <v-divider class="flex-shrink-0"></v-divider>
              <v-card-actions
                class="d-flex flex-column flex-md-row-reverse align-center pa-4 flex-shrink-0">
                <!-- TODO: Implement action button functionality -->
                <v-btn
                  v-if="showCompleteBtn"
                  variant="elevated"
                  size="x-large"
                  color="primary"
                  class="flex-fill w-100 w-md-auto typography-button-lg"
                  prepend-icon="mdi-check"
                  data-testid="circe-task-complete-btn"
                  :disabled="disableCompleteBtn"
                  @click="updateStatus">
                  Complete Task
                </v-btn>
                <v-btn
                  v-if="showStartBtn"
                  variant="elevated"
                  size="x-large"
                  color="primary"
                  class="flex-fill w-100 w-md-auto typography-button-lg"
                  prepend-icon="mdi-progress-clock"
                  data-testid="circe-task-start-btn"
                  :disabled="disableStartBtn"
                  @click="updateStatus">
                  Start Task
                </v-btn>
                <v-btn
                  v-if="showCancelBtn"
                  variant="outlined"
                  size="x-large"
                  color="error"
                  prepend-icon="mdi-close"
                  class="flex-fill w-100 w-md-auto typography-button-lg"
                  data-testid="circe-task-cancel-btn"
                  :disabled="disableCancelBtn"
                  @click="cancelTask">
                  Cancel Task
                </v-btn>
                <v-btn
                  v-if="showBackBtn"
                  size="x-large"
                  variant="outlined"
                  class="flex-fill w-100 w-md-auto typography-button-lg"
                  data-testid="circe-task-detail-back-to-list"
                  @click="closeTaskDetailAndClearRoute">
                  Back to Tasks
                </v-btn>
              </v-card-actions>
            </template>
          </v-card-text>
        </template>
        <template v-else>
          <div class="d-flex flex-column gap-3 justify-center align-center h-100">
            <v-icon icon="mdi-alert-circle" size="x-large" color="error"></v-icon>
            <span class="typography-body-md text--color-text-secondary">No task found</span>
            <v-btn
              variant="text"
              size="small"
              color="primary"
              @click="closeTaskDetailAndClearRoute">
              Close
            </v-btn>
          </div>
        </template>
      </template>

      <v-dialog v-model="showCompletionGuard" max-width="400">
        <v-card>
          <v-card-title>{{ t('checkTaskDetails.completionGuard.title') }}</v-card-title>
          <v-card-text>
            {{
              t(
                'checkTaskDetails.completionGuard.body',
                { count: pendingChecksCount },
                pendingChecksCount
              )
            }}
          </v-card-text>
          <v-card-actions class="d-flex flex-column ga-2 pa-4">
            <v-btn
              block
              variant="elevated"
              color="primary"
              class="typography-button-lg"
              data-testid="circe-task-completion-guard-confirm"
              :loading="isSaving"
              @click="executeComplete">
              {{ t('checkTaskDetails.completionGuard.confirm') }}
            </v-btn>
            <v-btn
              block
              variant="outlined"
              class="typography-button-lg"
              :disabled="isSaving"
              @click="showCompletionGuard = false">
              {{ t('checkTaskDetails.completionGuard.cancel') }}
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
    </v-card>
  </v-dialog>
</template>

<script setup lang="ts">
import { computed, ref, onMounted, onBeforeUnmount, watch, nextTick } from 'vue';
import { useI18n } from 'vue-i18n';
import { useFlag } from '@loadsmart/vue-feature-flags';
import { useYardInTransitFeature } from '@satellite/composables/yard/useYardInTransitFeature';
import { useCheckTaskPrimaryAction } from '@/composables/useCheckTaskPrimaryAction';
import { useRoute, useRouter } from 'vue-router';
import { useQueryClient } from '@tanstack/vue-query';
import { taskKeys } from '@/composables/queryKeys';
import {
  useEventHub,
  useTaskQueries,
  useYardTask,
  useDialogListener,
  useDialogTrigger,
  emitCustomEvent
} from '@/composables';
import {
  readYardTaskIdFromRouteQuery,
  YARD_TASK_DETAIL_QUERY_KEY
} from '@/helpers/yardTaskDetailRoute';
import { getCancelTaskDrawerContent } from '@/helpers/cancelTaskDrawerContent';
import { isAssetCheckTask, isInventoryCheckTask } from '@/helpers/checkTaskType';
import { useYardInventory } from '@/composables/useYardInventory';
import { useAuthStore } from '@/store/useAuthStore';
import { useWarehouseStore } from '@/store/useWarehouseStore';
import { useYardTaskAnalytics } from '@/analytics/yardTaskEvents';
import { isEmpty, isString } from 'lodash';
import { YardTaskStatus } from '@nova/core';
import {
  canStartYardTask,
  transformTaskApiResponseToViewData
} from '@satellite/helpers/yardTaskUtils';
import CheckTaskDetails from '@/components/CheckTaskDetails.vue';
import CustomTabs from '@satellite/components/elements/CustomTabs.vue';
import ElapsedTime from '@satellite/components/elements/ElapsedTime.vue';
import MoveTaskDetails from '@/components/MoveTaskDetails.vue';
import YardTaskTimeline from '@satellite/components/YardTaskTimeline/YardTaskTimeline.vue';
import YardTaskCommentForm from '@satellite/components/YardTaskTimeline/YardTaskCommentForm.vue';

import type { CheckTaskViewData, Maybe, MoveTaskViewData } from '@nova/core';

const BASE_TITLE = 'Yard Tasks - Opendock';

const { t } = useI18n();
const route = useRoute();
const router = useRouter();

const {
  isDialogOpen,
  dialogProps,
  closeDialog,
  resetDialogProps,
  subscribeToDialogEvent,
  unsubscribeFromDialogEvent
} = useDialogListener('open-edit-task-dialog');

function clearYardTaskIdFromRouteQuery() {
  if (route.name !== 'tasks') {
    return;
  }
  if (readYardTaskIdFromRouteQuery(route.query[YARD_TASK_DETAIL_QUERY_KEY]) == null) {
    return;
  }

  const restQuery = { ...route.query };
  delete restQuery[YARD_TASK_DETAIL_QUERY_KEY];
  void router.replace({ name: 'tasks', query: restQuery });
}

function syncTaskDetailDialogWithRoute() {
  if (route.name !== 'tasks') {
    return;
  }
  const yardTaskIdFromRoute = readYardTaskIdFromRouteQuery(route.query[YARD_TASK_DETAIL_QUERY_KEY]);
  if (yardTaskIdFromRoute) {
    if (dialogProps.value?.taskId !== yardTaskIdFromRoute || !isDialogOpen.value) {
      emitCustomEvent('open-edit-task-dialog', { taskId: yardTaskIdFromRoute });
    }
    return;
  }
  if (isDialogOpen.value) {
    closeDialog();
  }
}

function closeTaskDetailAndClearRoute() {
  clearYardTaskIdFromRouteQuery();
  closeDialog();
}

const { getTaskQuery, useUpdateTaskMutation } = useTaskQueries();
const warehouseStore = useWarehouseStore();
const { user } = useAuthStore();
const queryClient = useQueryClient();
const {
  trackYardTaskStarted,
  trackYardTaskCanceled,
  trackYardTaskCompleted,
  trackYardTaskAddedComment
} = useYardTaskAnalytics();
const taskId = computed(() => dialogProps.value?.taskId || '');

const taskQuery = getTaskQuery(taskId, {
  enabled: computed(() => Boolean(taskId.value) && isDialogOpen.value)
});

const task = computed(() => {
  const raw = taskQuery.data.value;
  if (!raw) {
    return undefined;
  }

  return transformTaskApiResponseToViewData(raw as any) || undefined;
});

const eventHub = useEventHub();

const {
  taskNumber,
  getStatusLabel,
  getStatusIcon,
  getStatusIconColor,
  isMoveTask,
  isCheckTask,

  isTaskCreated,
  isTaskInProgress,
  isTaskCompleted,
  isTaskCancelled,

  inProgressEvent,
  completedEvent,
  cancelledEvent
} = useYardTask({ initialTask: task });

// Type-narrowed computed properties for components
const moveTask = computed(() => {
  return isMoveTask.value && task.value ? (task.value as MoveTaskViewData) : null;
});

const checkTask = computed(() => {
  return isCheckTask.value && task.value ? (task.value as CheckTaskViewData) : null;
});

const inventoryScopeTaskId = computed(() =>
  checkTask.value && isInventoryCheckTask(checkTask.value) ? taskId.value : ''
);
const inventoryScope = useYardInventory({
  taskId: inventoryScopeTaskId,
  warehouseId: computed(() => checkTask.value?.warehouseId ?? ''),
  headless: true
});

const pendingChecksCount = computed(() => {
  const currentCheckTask = checkTask.value;
  if (!currentCheckTask) {
    return 0;
  }
  if (isAssetCheckTask(currentCheckTask)) {
    return isEmpty(currentCheckTask.checkAssetContainers) ? 1 : 0;
  }
  if (isInventoryCheckTask(currentCheckTask)) {
    return inventoryScope.warehouseCounts.value.pending;
  }
  return 0;
});

const statusLabel = computed(() => {
  if (task.value) {
    return getStatusLabel(task.value.status);
  }

  return '';
});

const disableCancelBtn = computed(() => {
  return isLoading.value || isSaving.value;
});

const showCancelBtn = computed(() => {
  return isTaskInProgress.value;
});

const disableCompleteBtn = computed(() => {
  return isLoading.value || isSaving.value || !allowComplete.value;
});

const showCompleteBtn = computed(() => {
  return task.value && isTaskInProgress.value;
});

const { isYardInTransitEnabled } = useYardInTransitFeature();

const disableStartBtn = computed(() => {
  return isLoading.value || (isYardInTransitEnabled.value && !canStartYardTask(task.value));
});

const showStartBtn = computed(() => {
  return task.value && isTaskCreated.value;
});

const showBackBtn = computed(() => {
  return isTaskCompleted.value || isTaskCancelled.value;
});

const statusIcon = computed(() => {
  return getStatusIcon(task.value?.status);
});

const statusIconColor = computed(() => {
  return getStatusIconColor(task.value?.status);
});

const warehouseTimezone = computed(() => {
  return warehouseStore.selectedWarehouseTimezone.value || 'utc';
});

const updateTaskMutation = useUpdateTaskMutation({
  onSuccess: data => {
    dialogProps.value?.onTaskUpdated?.(data);
  }
});

const { triggerPrimaryAction } = useCheckTaskPrimaryAction();

const isLoading = computed(() => taskQuery.isPending.value);

const isSaving = computed(() => updateTaskMutation.isPending.value);

/**
 * Inventory check tasks are warehouse-scoped, so they have no `relatedObject` for
 * `triggerPrimaryAction` to key on — open the inventory panel directly, mirroring how
 * asset/spot checks auto-open their panel on start.
 */
function openInventoryPanelOnStart(inventoryTask: CheckTaskViewData): void {
  const checkTaskId = inventoryTask.id;
  const checkId = inventoryTask.checkId;
  const warehouseId = inventoryTask.warehouseId;
  if (!checkTaskId || !checkId || !warehouseId) {
    return;
  }
  emitCustomEvent('open-check-spot-area-inventory', {
    taskId: checkTaskId,
    checkId,
    spotAreaId: warehouseId,
    warehouseId,
    taskStatus: YardTaskStatus.IN_PROGRESS,
    checkAssetContainers: inventoryTask.checkAssetContainers
  });
}

const executeComplete = () => {
  if (!task.value || !user?.id) {
    return;
  }

  const isStarting = isTaskCreated.value;
  if (!isTaskInProgress.value && !isStarting) {
    return;
  }

  const params = isTaskInProgress.value
    ? { status: YardTaskStatus.COMPLETED }
    : { status: YardTaskStatus.IN_PROGRESS, assignedTo: user.id };

  updateTaskMutation.mutate(
    { taskId: taskId.value, params },
    {
      onSuccess: () => {
        const currentTask = task.value;
        if (!currentTask) {
          return;
        }
        if (isTaskInProgress.value) {
          trackYardTaskCompleted(currentTask);
        } else {
          trackYardTaskStarted(currentTask, {
            additionalPayload: { yard_task_assignee: user.id }
          });
        }
        if (isStarting) {
          const currentCheckTask = checkTask.value;
          if (currentCheckTask && isInventoryCheckTask(currentCheckTask)) {
            openInventoryPanelOnStart(currentCheckTask);
          } else if (currentCheckTask) {
            triggerPrimaryAction(currentCheckTask);
          }
        }
        showCompletionGuard.value = false;
      }
    }
  );
};

const updateStatus = () => {
  if (!task.value) {
    console.error('Task not found');
    return;
  }

  if (!user?.id) {
    console.error('User not found');
    return;
  }

  if (isTaskInProgress.value && pendingChecksCount.value > 0) {
    showCompletionGuard.value = true;
    return;
  }

  executeComplete();
};

const { triggerDialog: openCancelTaskDrawer } = useDialogTrigger('open-cancel-task-drawer');
const cancelReasonFlag = useFlag('enable-yard-task-cancel-reason');
const isCancelReasonEnabled = computed(() => Boolean(cancelReasonFlag.value.enabled));

const cancelTask = () => {
  const currentTask = task.value;
  if (!currentTask) {
    return;
  }

  if (!isCancelReasonEnabled.value) {
    updateTaskMutation.mutate(
      {
        taskId: taskId.value,
        params: {
          status: YardTaskStatus.CANCELLED
        }
      },
      {
        onSuccess: () => {
          trackYardTaskCanceled(currentTask);
        }
      }
    );
    return;
  }

  openCancelTaskDrawer({
    ...getCancelTaskDrawerContent(currentTask, t),
    onConfirm: async (reason: string) => {
      await updateTaskMutation.mutateAsync({
        taskId: taskId.value,
        params: { status: YardTaskStatus.CANCELLED, reason }
      });
      trackYardTaskCanceled(currentTask, {
        additionalPayload: { cancellation_reason: reason }
      });
    }
  });
};

const isOpen = computed(() => isDialogOpen.value);

const activeTab = ref('task');
const allowComplete = ref(false);
const showCompletionGuard = ref(false);
const activityScrollRef = ref<HTMLElement | null>(null);

const scrollActivityToBottom = async () => {
  await nextTick();
  const el = activityScrollRef.value;
  if (el) {
    el.scrollTop = el.scrollHeight;
  }
};

watch(activeTab, tab => {
  if (tab === 'activity') {
    scrollActivityToBottom();
  }
});

const handleAllowComplete = (allow: boolean) => {
  allowComplete.value = allow;
};

const handleYardTaskChange = async () => {
  if (taskId.value) {
    await queryClient.invalidateQueries({ queryKey: taskKeys.detail(taskId.value) });
  }
};

const taskAssetId = computed((): Maybe<string> => checkTask.value?.relatedObject?.id ?? null);

const refreshIfTaskAsset = async (candidateAssetId: Maybe<string>): Promise<void> => {
  const target = taskAssetId.value;
  if (isString(target) && target === candidateAssetId) {
    await handleYardTaskChange();
  }
};

const handleAssetContainerChange = (event: { id?: string }) => refreshIfTaskAsset(event?.id);
const handleSpotAssignmentChange = (event: { assetContainerId?: string }) =>
  refreshIfTaskAsset(event?.assetContainerId);

const handleCommentAdded = () => {
  if (task.value) {
    trackYardTaskAddedComment(task.value);
  }
  scrollActivityToBottom();
};

watch(
  [isOpen, task],
  ([isOpen, currentTask]) => {
    if (isOpen && currentTask?.refNum) {
      document.title = `Yard Task #${currentTask.refNum} - Opendock`;
    } else {
      activeTab.value = 'task';
      showCompletionGuard.value = false;
      document.title = BASE_TITLE;
    }
  },
  { immediate: true }
);

watch(
  () => route.query[YARD_TASK_DETAIL_QUERY_KEY],
  () => {
    syncTaskDetailDialogWithRoute();
  }
);

onMounted(() => {
  subscribeToDialogEvent();
  syncTaskDetailDialogWithRoute();
  eventHub.on('update-YardTask', handleYardTaskChange);
  eventHub.on('update-AssetContainer', handleAssetContainerChange);
  eventHub.on('create-SpotAssignment', handleSpotAssignmentChange);
  eventHub.on('update-SpotAssignment', handleSpotAssignmentChange);
  eventHub.on('delete-SpotAssignment', handleSpotAssignmentChange);
});

onBeforeUnmount(() => {
  unsubscribeFromDialogEvent();
  eventHub.off('update-YardTask', handleYardTaskChange);
  eventHub.off('update-AssetContainer', handleAssetContainerChange);
  eventHub.off('create-SpotAssignment', handleSpotAssignmentChange);
  eventHub.off('update-SpotAssignment', handleSpotAssignmentChange);
  eventHub.off('delete-SpotAssignment', handleSpotAssignmentChange);
  document.title = BASE_TITLE;
});
</script>

<style lang="scss" scoped>
/**
 * A fullscreen dialog is 100% of a fixed overlay, i.e. the viewport with the browser toolbar
 * hidden. Chrome for iOS resolves `vh` and `dvh` the same way — it is WebKit, and WebKit sizes
 * those for Safari's UI, not Chrome's — so the dialog runs under the toolbar and the page keeps
 * enough overflow for Chrome to scroll it. `svh` is the only unit that accounts for the toolbar.
 */
:deep(.task-detail-card) {
  height: 100%;
  height: 100svh;
  max-height: 100svh;
}

.v-card-title {
  background-color: $color-neutral-20;
  min-block-size: 60px;

  .back-btn {
    background-color: $color-neutral-20;
  }

  &.in-progress {
    background-color: $color-primary-20;

    .back-btn {
      background-color: $color-primary-20;
    }
  }

  &.completed {
    background-color: $color-success-20;

    .back-btn {
      background-color: $color-success-20;
    }
  }

  &.cancelled {
    opacity: 1;
  }
}

:deep(.custom-tabs) {
  background-color: $color-neutral-0;

  .v-tab {
    flex: 1;
  }
}

:deep(.custom-tabs-window) {
  flex-grow: 1;

  .v-window__container {
    height: 100%;
  }
}

.v-card-actions {
  background: $color-neutral-0;
}

.activity-scroll {
  min-height: 0;
}

.completion-banner {
  background-color: rgb(var(--v-theme-neutral-0));
  border-block-end: 1px solid rgb(var(--v-theme-neutral-40));
}
</style>
